import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { parsePackQuantity, planRenewalCoverage } from '../../utils/prescriptionRenewalSheets';

export interface PrescriptionRenewalAssistProps {
  firstItemQuantity?: string;
  firstItemPackageCount?: number;
  /** @deprecated use firstItemPackageCount */
  firstItemTwoPackages?: boolean;
  returnMonths: number;
  onReturnMonthsChange: (n: number) => void;
  tabletsPerMonth: number;
  onTabletsPerMonthChange: (n: number) => void;
  currentMonthCount: number;
  currentMonthStep?: number;
  onApplySuggestedMonths: (months: number, monthStep: number) => void;
  disabled?: boolean;
}

const PrescriptionRenewalAssist: React.FC<PrescriptionRenewalAssistProps> = ({
  firstItemQuantity,
  firstItemPackageCount = 1,
  firstItemTwoPackages = false,
  returnMonths,
  onReturnMonthsChange,
  tabletsPerMonth,
  onTabletsPerMonthChange,
  currentMonthCount,
  currentMonthStep = 1,
  onApplySuggestedMonths,
  disabled = false,
}) => {
  const pack = parsePackQuantity(firstItemQuantity);
  const packageCount = Math.max(1, firstItemPackageCount || (firstItemTwoPackages ? 2 : 1));
  const plan = pack
    ? planRenewalCoverage({ returnMonths, packQuantity: pack, tabletsPerMonth, packageCount })
    : null;
  const alreadyApplied = plan != null
    && currentMonthCount === plan.sheets
    && (plan.sheets <= 1 || currentMonthStep === plan.monthStep);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px dashed #93c5fd',
        bgcolor: '#f0f9ff',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#0F766E', letterSpacing: 0.4 }}>
        RENOVAÇÃO
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
        Calcule quantas folhas imprimir na mesma receita conforme o retorno e a quantidade na embalagem do 1º medicamento.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
        <TextField
          size="small"
          type="number"
          label="Retorno (meses)"
          value={returnMonths}
          disabled={disabled}
          onChange={(e) => {
            const n = Math.floor(Number(e.target.value));
            onReturnMonthsChange(Number.isFinite(n) ? Math.max(1, Math.min(24, n)) : 6);
          }}
          inputProps={{ min: 1, max: 24, step: 1 }}
          sx={{ width: 140 }}
        />
        <TextField
          size="small"
          type="number"
          label="Comprimidos / mês"
          value={tabletsPerMonth}
          disabled={disabled}
          onChange={(e) => {
            const n = Math.floor(Number(e.target.value));
            onTabletsPerMonthChange(Number.isFinite(n) ? Math.max(1, Math.min(120, n)) : 30);
          }}
          inputProps={{ min: 1, max: 120, step: 1 }}
          sx={{ width: 160 }}
        />
        {plan != null && (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
            Sugestão: <strong>{plan.sheets}</strong> folha{plan.sheets > 1 ? 's' : ''}
            {plan.sheets > 1 && plan.monthStep > 1 ? `, uma a cada ${plan.monthStep} meses` : ''}
            {` (${plan.unitsPerSheet} un. por folha)`}
            {alreadyApplied ? ' — já aplicado' : ''}
          </Typography>
        )}
        {plan?.insufficient && (
          <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 600 }}>
            {plan.unitsPerSheet} un. cobrem só ~{plan.daysPerSheet} dias por mês.
            Aumente o número de embalagens ou a quantidade na embalagem.
          </Typography>
        )}
        {pack == null && (
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Informe a quantidade no 1º medicamento para ver a sugestão.
          </Typography>
        )}
        <Button
          size="small"
          variant="contained"
          disabled={disabled || plan == null || alreadyApplied}
          onClick={() => plan != null && onApplySuggestedMonths(plan.sheets, plan.monthStep)}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
        >
          Aplicar em «Meses na impressão»
        </Button>
      </Box>
    </Box>
  );
};

export default PrescriptionRenewalAssist;
